import React, { useEffect, useState } from 'react';
import { Button, Stack, Text, useToast } from '@chakra-ui/react';
import { useNavigate, useParams } from 'react-router-dom';
import { CatalogueImage } from '../../utils/types';
import {
  deleteCatalogueItemImage,
  fetchCatalogueItemImages,
  uploadCatalogueItemImage,
} from '../../hooks/catalogueItemImages';
import CatalogueMediaManager from './components/CatalogueMediaManager';
import {
  CatalogueHero,
  CatalogueListSkeleton,
  CatalogueShell,
} from './components/catalogueUi';

const CatalogueItemImages = () => {
  const { orgId, itemId, catalogueId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [images, setImages] = useState<CatalogueImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const loadImages = async () => {
    if (!orgId || !catalogueId || !itemId) {
      return;
    }

    setLoading(true);
    const response = await fetchCatalogueItemImages(orgId, catalogueId, itemId);
    setLoading(false);

    if (!response.success) {
      toast({
        title: 'Unable to load media',
        description: 'Please try again in a moment.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setImages(response.data || []);
  };

  useEffect(() => {
    loadImages();
  }, [orgId, catalogueId, itemId]);

  const handleUpload = async (file: File) => {
    if (!orgId || !catalogueId || !itemId) {
      return;
    }

    setUploading(true);
    const response = await uploadCatalogueItemImage(orgId, itemId, catalogueId, file);
    setUploading(false);

    if (!response.success) {
      toast({
        title: 'Upload failed',
        description: 'We could not upload this image.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Image uploaded',
      description: 'The media library has been updated.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    loadImages();
  };

  const handleDelete = async (imageId: string) => {
    if (!orgId || !catalogueId || !itemId) {
      return;
    }

    const response = await deleteCatalogueItemImage(
      orgId,
      catalogueId,
      itemId,
      imageId,
    );

    if (!response.success) {
      toast({
        title: 'Delete failed',
        description: 'We could not remove this image.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Image deleted',
      status: 'success',
      duration: 2500,
      isClosable: true,
    });
    loadImages();
  };

  return (
    <CatalogueShell compact>
      <CatalogueHero
        eyebrow="Media"
        title="Manage item imagery with less friction"
        description="Upload, review, and remove product images in one place. The first image stays the catalogue cover for this item."
        secondaryAction={
          <Button
            borderRadius="16px"
            variant="outline"
            onClick={() =>
              navigate(
                `/organization/${orgId}/catalogue/${catalogueId}/catalogue-item/${itemId}`,
              )
            }
          >
            Back to item
          </Button>
        }
      />

      {loading ? (
        <CatalogueListSkeleton />
      ) : (
        <Stack spacing={6}>
          <CatalogueMediaManager
            images={images}
            onUpload={handleUpload}
            onDelete={handleDelete}
            uploading={uploading}
            title="Image gallery"
            description="Use this gallery to keep the item visually complete and ready for mobile browsing."
          />
          {!images.length ? (
            <Text fontSize="sm" color="gray.500">
              Add at least one image so this item reads as complete in the catalogue list.
            </Text>
          ) : null}
        </Stack>
      )}
    </CatalogueShell>
  );
};

export default CatalogueItemImages;
